/**
 * Quick Add Modal — Swatch AJAX Updater
 * ======================================
 * Purpose:
 *   Prevents page redirects when a metafield-based cross-product color swatch is
 *   clicked inside the quick-add-modal. Instead, it fetches the target product's
 *   HTML via Shopify's Section Rendering API, re-injects it into the modal, and
 *   re-attaches all Shopify JS hooks — keeping the popup open the whole time.
 *
 * How it works:
 *   1. Event delegation on <quick-add-modal> catches clicks on .color-swatch
 *      divs that carry a [data-product-handle] attribute.
 *   2. The clicked swatch's handle is used to build a Section Rendering URL:
 *        /products/{handle}?section_id={sectionId}
 *      This returns only the rendered section HTML (not the full page).
 *   3. The fetched HTML is parsed, IDs are de-duped (matching quick-add.js logic),
 *      and the modal's content element is replaced.
 *   4. After injection all inline <script> tags are re-executed, PaymentButton and
 *      ProductModel are re-init'd, and the URL bar is NOT updated.
 *
 * Placement:
 *   Load this file via a <script> tag in quick-add.js's companion section, or
 *   include it in theme.liquid / base.liquid AFTER quick-add.js loads.
 *   e.g.:  <script src="{{ 'quick-add-modal-swatch.js' | asset_url }}" defer></script>
 *
 * No Liquid changes are required for regular variant radio/button pickers — those
 * are already handled by Shopify's variant-radios custom element + product-info.js.
 * This file only adds the missing AJAX layer for cross-product metafield swatches.
 */

(function () {
    'use strict';

    // ─── Constants ────────────────────────────────────────────────────────────────

    /** Attribute we add to each cross-product swatch <div> via Liquid */
    const HANDLE_ATTR = 'data-product-handle';

    /** CSS class used on the wrapping <div> for cross-product swatches */
    const SWATCH_CLASS = 'color-swatch';

    /** Loading indicator class injected into the modal while fetching */
    const LOADING_CLASS = 'quick-add-modal--swatch-loading';

    // ─── Helpers ──────────────────────────────────────────────────────────────────

    /**
     * Re-execute all inline <script> tags inside an element.
     * innerHTML assignment silently skips script execution; this fixes that.
     * @param {HTMLElement} element
     */
    function reinjectScripts(element) {
        element.querySelectorAll('script').forEach((oldScript) => {
            const newScript = document.createElement('script');
            Array.from(oldScript.attributes).forEach((attr) =>
                newScript.setAttribute(attr.name, attr.value)
            );
            newScript.textContent = oldScript.textContent;
            oldScript.parentNode.replaceChild(newScript, oldScript);
        });
    }

    /**
     * Re-run Shopify's payment button and 3D model initialisers after modal refresh.
     */
    function reinitShopifyFeatures() {
        if (window.Shopify && Shopify.PaymentButton) {
            Shopify.PaymentButton.init();
        }
        if (window.ProductModel) {
            window.ProductModel.loadShopifyXR();
        }
    }

    /**
     * De-duplicate element IDs the same way quick-add.js does it in
     * preventDuplicatedIDs(), so Shopify custom elements can find their targets.
     * @param {HTMLElement} productEl  – the <section id="MainProduct-..."> element
     * @returns {string}  sectionId extracted from the element
     */
    function deduplicateIDs(productEl) {
        const sectionId = productEl.dataset.section;
        productEl.innerHTML = productEl.innerHTML.replaceAll(
            sectionId,
            `quickadd-${sectionId}`
        );
        productEl
            .querySelectorAll('variant-selects, variant-radios, product-info')
            .forEach((el) => {
                el.dataset.originalSection = sectionId;
            });
        return sectionId;
    }

    /**
     * Remove elements that don't belong inside a quick-add modal (same as
     * quick-add.js's removeDOMElements).
     * @param {HTMLElement} productEl
     */
    function removeUnwantedElements(productEl) {
        ['pickup-availability', 'product-modal'].forEach((tag) => {
            const el = productEl.querySelector(tag);
            if (el) el.remove();
        });
        productEl.querySelectorAll('modal-dialog').forEach((el) => el.remove());
    }

    /**
     * Disable URL switching on the freshly-injected variant picker so that
     * selecting a size/option inside the modal doesn't navigate away.
     * @param {HTMLElement} modalContent
     */
    function preventVariantURLSwitching(modalContent) {
        const picker = modalContent.querySelector('variant-radios, variant-selects');
        if (picker) picker.setAttribute('data-update-url', 'false');
    }

    /**
     * Mark the currently-active cross-product color swatch inside the modal.
     * @param {HTMLElement} modalContent
     * @param {string} activeHandle   – the handle of the product now shown
     */
    function setActiveColorSwatch(modalContent, activeHandle) {
        modalContent.querySelectorAll(`.${SWATCH_CLASS}[${HANDLE_ATTR}]`).forEach((swatch) => {
            const isActive = swatch.getAttribute(HANDLE_ATTR) === activeHandle;
            swatch.classList.toggle('is-active', isActive);
            const radio = swatch.querySelector('input[type="radio"]');
            if (radio) radio.checked = isActive;
        });
    }

    // ─── Variant Initializer ─────────────────────────────────────────────────────

    /**
     * After AJAX content is injected, Shopify's variant-radios custom element
     * already exists in the DOM but its onVariantChange() hasn't fired yet
     * (the element upgrade ran during reinjectScripts, but no radio change
     * event was dispatched). This function:
     *   1. Verifies at least one radio is checked per option fieldset
     *      (Liquid pre-checks the first available, but we ensure it in JS too)
     *   2. Dispatches a synthetic 'change' event on variant-radios so
     *      onVariantChange() → updateMasterId() → updateVariantInput() runs,
     *      setting the correct variant ID in the hidden input[name="id"]
     *
     * @param {HTMLElement} modalContent – the [id^="QuickAddInfo-"] container
     */
    function ensureVariantInitialized(modalContent) {
        const variantPicker = modalContent.querySelector('variant-radios, variant-selects');
        if (!variantPicker) return;

        // Step 1: For each option fieldset (skip the metafield color fieldset which
        // has cross-product swatches — those have [data-product-handle] on the div
        // wrapping them). Only look at standard variant option fieldsets.
        const fieldsets = Array.from(variantPicker.querySelectorAll('fieldset'));
        fieldsets.forEach((fieldset) => {
            const radios = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
            if (!radios.length) return;

            // Skip fieldsets whose radios are cross-product metafield swatches
            // (their parent .color-swatch div has data-product-handle)
            const isCrossProductFieldset = radios.every((r) =>
                r.closest('[data-product-handle]') !== null
            );
            if (isCrossProductFieldset) return;

            // If no radio is checked, check the first non-disabled one
            const hasChecked = radios.some((r) => r.checked);
            if (!hasChecked) {
                const firstAvail = radios.find((r) => !r.disabled && !r.classList.contains('disabled'));
                if (firstAvail) firstAvail.checked = true;
            }
        });

        // Step 2: Fire a single 'change' event on the variant-radios element.
        // Also fire 'input' to be sure all listeners catch it.
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                try {
                    variantPicker.dispatchEvent(new Event('change', { bubbles: true }));
                    variantPicker.dispatchEvent(new Event('input', { bubbles: true }));

                    // Force a master ID update if the custom element has the method
                    if (typeof variantPicker.updateMasterId === 'function') {
                        variantPicker.updateMasterId();
                    }
                } catch (err) {
                    console.warn('[QuickAddModalSwatch] Could not trigger variant change:', err);
                }
            });
        });
    }

    // ─── Core Modal Updater ───────────────────────────────────────────────────────

    /**
     * Fetch rendered product HTML and swap the modal content.
     *
     * @param {QuickAddModal-like HTMLElement} modal  – <quick-add-modal>
     * @param {HTMLElement} clickedSwatch             – the .color-swatch div clicked
     * @param {string} productHandle                  – e.g. "my-blue-shirt"
     */
    function loadProductIntoModal(modal, clickedSwatch, productHandle) {
        const modalContent = modal.querySelector('[id^="QuickAddInfo-"]');
        if (!modalContent) return;

        // Find the section ID from the currently-rendered content.
        // Quick-add.js replaces IDs but stores the original section ID on 
        // variant pickers and product-info elements as data-original-section.
        const picker = modalContent.querySelector('variant-radios, variant-selects, product-info');
        const sectionId = picker
            ? (picker.dataset.originalSection || picker.dataset.section)
            : modalContent.querySelector('[data-section]')?.dataset.section;

        if (!sectionId) {
            console.warn('[QuickAddModalSwatch] Could not detect sectionId — aborting AJAX update.');
            return;
        }

        // Visual feedback: add a loading class and disable the swatch
        modal.classList.add(LOADING_CLASS);
        clickedSwatch.setAttribute('aria-busy', 'true');

        // Build the Section Rendering API URL.
        // This returns just the section's HTML — no full page reload.
        const fetchURL = `/products/${productHandle}?section_id=${sectionId}`;

        fetch(fetchURL)
            .then((response) => {
                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                return response.text();
            })
            .then((html) => {
                const parsed = new DOMParser().parseFromString(html, 'text/html');

                // The Section Rendering API wraps the section in a <div id="shopify-section-...">
                // so we look for our MainProduct section inside it.
                let productEl = parsed.querySelector('section[id^="MainProduct-"]');

                if (!productEl) {
                    // Fallback: some themes wrap differently — grab the first section child
                    const wrapper = parsed.querySelector('[id^="shopify-section-"]');
                    productEl = wrapper ? wrapper.firstElementChild : null;
                }

                if (!productEl) {
                    console.warn('[QuickAddModalSwatch] Could not find MainProduct section in response.');
                    return;
                }

                // Apply the same transformations quick-add.js does on first load
                removeUnwantedElements(productEl);
                deduplicateIDs(productEl);

                // Swap content
                modalContent.innerHTML = productEl.innerHTML;

                // Re-execute scripts, re-init Shopify features
                reinjectScripts(modalContent);
                reinitShopifyFeatures();

                // Prevent URL changes when the user picks a size option
                preventVariantURLSwitching(modalContent);

                // Mark the now-active color swatch
                setActiveColorSwatch(modalContent, productHandle);

                // ── Trigger variant initialization ─────────────────────────────────
                // The freshly-injected variant-radios element needs to fire its change
                // event so VariantSelects.onVariantChange() runs, setting the correct
                // variant ID in the form, updating price, and updating availability.
                ensureVariantInitialized(modalContent);
            })
            .catch((err) => {
                console.error('[QuickAddModalSwatch] Failed to load product:', err);
            })
            .finally(() => {
                modal.classList.remove(LOADING_CLASS);
                clickedSwatch.removeAttribute('aria-busy');
            });
    }

    // ─── Event Delegation ─────────────────────────────────────────────────────────

    /**
     * Attach a single delegated click listener to the document.
     * This fires for any .color-swatch[data-product-handle] anywhere.
     */
    function attachSwatchInterceptor() {
        document.addEventListener('click', function (event) {
            // Walk up from the click target to find a .color-swatch with handle
            const swatch = event.target.closest(`.${SWATCH_CLASS}[${HANDLE_ATTR}]`);
            if (!swatch) return;

            const handle = swatch.getAttribute(HANDLE_ATTR);
            if (!handle) return;

            // Only intercept if we are inside a quick-add-modal
            const modal = swatch.closest('quick-add-modal');

            if (modal) {
                // Inside Modal: Prevent redirect and load via AJAX
                event.preventDefault();
                event.stopPropagation();

                if (swatch.classList.contains('is-active')) return;

                loadProductIntoModal(modal, swatch, handle);
            } else {
                // Outside Modal: Manual redirect (since we removed onclick from Liquid)
                // This ensures swatches still work on normal product pages.
                window.location.href = `/products/${handle}`;
            }
        }, true /* capture phase to intercept before other handlers */);
    }

    // ─── Loading Indicator CSS ────────────────────────────────────────────────────

    /**
     * Inject minimal CSS for the loading state so we give visual feedback
     * without requiring a separate stylesheet edit.
     */
    function injectLoadingStyles() {
        if (document.getElementById('qam-swatch-styles')) return;
        const style = document.createElement('style');
        style.id = 'qam-swatch-styles';
        style.textContent = `
      quick-add-modal.${LOADING_CLASS} [id^="QuickAddInfo-"] {
        opacity: 0.5;
        pointer-events: none;
        transition: opacity 0.2s ease;
      }
      quick-add-modal.${LOADING_CLASS} [id^="QuickAddInfo-"]::after {
        content: '';
        position: absolute;
        inset: 0;
        background: transparent;
        cursor: wait;
      }
      .color-swatch[aria-busy="true"] {
        opacity: 0.6;
        cursor: wait;
      }
    `;
        document.head.appendChild(style);
    }

    // ─── Bootstrap ────────────────────────────────────────────────────────────────

    function init() {
        injectLoadingStyles();
        attachSwatchInterceptor();
    }

    // Run after DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
